import { useState, useCallback, useContext, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  Button,
  Table,
  ButtonIcon,
  TrashIcon,
  LoadingSpinner,
} from '@rabbit/elements/shared-components';
import { toast } from 'react-toastify';
import {
  DTVendable,
  FBD_Vendable,
  PersonaIdTypeSplitter,
  PersonaTypeSingleLetter,
} from '@rabbit/data/types';
import { AppContext } from '@rabbit/app-context';
import { updateDocument } from '@rabbit/utils/api';
import { Formik, Form } from 'formik';
import {
  nestApiUpdateVendableInAlgolia,
  nestApiUpdateMultipleVendablesInAlgolia,
} from '@rabbit/bizproc/core';
import * as XLSX from 'xlsx';

interface SerialNumberUploadModalProps {
  onClose: () => void;
  vendable: DTVendable | null;
}

interface MpnSerialNumberMap {
  mpn: string;
  serialNumbers: string[];
  exists: boolean;
  checked?: boolean;
}

export const SerialNumberUploadModal: React.FC<
  SerialNumberUploadModalProps
> = ({ onClose, vendable }) => {
  const { t } = useTranslation();
  const [parsedData, setParsedData] = useState<MpnSerialNumberMap[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { tenantInfo } = useContext(AppContext);

  // Function to check if an MPN exists in the vendable collection
  const checkMpnExists = async (mpn: string): Promise<boolean> => {
    try {
      const tenantLink = t('tenantLink');
      const manufacturerPersona =
        PersonaTypeSingleLetter.Manufacturer +
        PersonaIdTypeSplitter +
        tenantLink;
      const vendables = await FBD_Vendable.query()
        .where('mpn', '==', mpn)
        .where('mfr', '==', manufacturerPersona)
        .getContents();
      return vendables.length > 0;
    } catch (error) {
      console.error('Error checking if MPN exists:', error);
      return false;
    }
  };

  // Function to fetch vendable document by MPN
  const getVendableByMpn = async (mpn: string): Promise<DTVendable | null> => {
    try {
      const tenantLink = t('tenantLink');
      const manufacturerPersona =
        PersonaTypeSingleLetter.Manufacturer +
        PersonaIdTypeSplitter +
        tenantLink;
      const vendables = await FBD_Vendable.query()
        .where('mpn', '==', mpn)
        .where('mfr', '==', manufacturerPersona)
        .getContents();

      if (vendables.length > 0) {
        return vendables[0];
      }
      return null;
    } catch (error) {
      console.error('Error fetching vendable by MPN:', error);
      return null;
    }
  };

  // Check if MPNs exist in the vendable collection
  useEffect(() => {
    const checkMpns = async () => {
      if (parsedData.length === 0) return;

      const updatedData = [...parsedData];
      let dataChanged = false;

      for (let i = 0; i < updatedData.length; i++) {
        const item = updatedData[i];
        if (item.checked) continue;

        setIsLoading(true);
        const exists = await checkMpnExists(item.mpn);
        item.exists = exists;
        item.checked = true;
        dataChanged = true;
      }

      if (dataChanged) {
        setParsedData(updatedData);
        setIsLoading(false);
      }
    };

    checkMpns();
  }, [parsedData]);

  // Initial values for Formik
  const initialValues = {};

  // Function to handle file selection
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      parseFile(file);
    }
  };

  // Function to handle download of sample file
  const handleDownloadSampleFile = () => {
    // Sample CSV content with comma delimiter
    // The file demonstrates that we support comma, semicolon, tab, and pipe delimiters
    const fileContent =
      '# This is a sample file showing different delimiter options\n' +
      '# Option 1: Comma-separated (,)\n' +
      'MPN1,MPN2,MPN3\n' +
      'SN001,SN004,SN007\n' +
      'SN002,SN005,SN008\n' +
      'SN003,SN006,SN009\n\n' +
      '# Option 2: Semicolon-separated (;)\n' +
      '# MPN1;MPN2;MPN3\n' +
      '# SN001;SN004;SN007\n' +
      '# SN002;SN005;SN008\n' +
      '# SN003;SN006;SN009\n\n' +
      '# Option 3: Tab-separated (\\t)\n' +
      '# MPN1\tMPN2\tMPN3\n' +
      '# SN001\tSN004\tSN007\n' +
      '# SN002\tSN005\tSN008\n' +
      '# SN003\tSN006\tSN009\n\n' +
      '# Option 4: Pipe-separated (|)\n' +
      '# MPN1|MPN2|MPN3\n' +
      '# SN001|SN004|SN007\n' +
      '# SN002|SN005|SN008\n' +
      '# SN003|SN006|SN009\n';

    // Create a Blob from the CSV content
    const blob = new Blob([fileContent], { type: 'text/csv;charset=utf-8;' });

    // Create a URL for the Blob
    const url = URL.createObjectURL(blob);

    // Create a link element
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Sample File.csv';

    // Append to the document
    document.body.appendChild(link);

    // Trigger the download
    link.click();

    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Function to parse CSV or XLSX file
  const parseFile = useCallback(
    (file: File) => {
      try {
        setIsLoading(true);

        const fileExtension = file.name.split('.').pop()?.toLowerCase();

        // Handle Numbers files
        if (fileExtension === 'numbers') {
          setIsLoading(false);
          toast.info(
            'Apple Numbers files (.numbers) need to be exported to CSV or Excel format before uploading. Please export your file and try again.'
          );
          return;
        }

        // Handle XLSX files
        if (fileExtension === 'xlsx') {
          const reader = new FileReader();

          reader.onload = (e) => {
            try {
              const data = e.target?.result;
              if (!data) {
                toast.error(t('message.errorParsingCsvFile'));
                setIsLoading(false);
                return;
              }

              // Parse the XLSX file
              const workbook = XLSX.read(data, { type: 'array' });

              // Get the first sheet
              const sheetName = workbook.SheetNames[0];
              const worksheet = workbook.Sheets[sheetName];

              // Convert to array of arrays
              const rows = XLSX.utils.sheet_to_json(worksheet, {
                header: 1,
              }) as any[][];

              if (rows.length === 0) {
                toast.error(t('message.csvFileIsEmpty'));
                setIsLoading(false);
                return;
              }

              // Parse the data
              const parsedData: MpnSerialNumberMap[] = [];
              const mpnMap = new Map<string, string[]>();
              const mpnArray: string[] = []; // Store MPNs in order to maintain column mapping

              // Determine header row: find first non-empty, non-comment row
              const isRowComment = (row: any[]): boolean => {
                return row[0] && String(row[0]).trim().startsWith('#');
              };
              const isRowAllEmpty = (row: any[]): boolean => {
                return row.every(
                  (cell) => cell === undefined || String(cell).trim() === ''
                );
              };

              let headerRowIndex = -1;
              for (let i = 0; i < rows.length; i++) {
                const row = rows[i];
                if (row.length === 0 || isRowComment(row)) continue;
                if (!isRowAllEmpty(row)) {
                  headerRowIndex = i;
                  break;
                }
              }

              if (headerRowIndex === -1) {
                toast.error(t('message.csvFileIsEmpty'));
                setIsLoading(false);
                return;
              }

              // Build MPNs from header row
              const headerRow = rows[headerRowIndex];
              headerRow.forEach((value) => {
                if (value === undefined || String(value).trim() === '') return;
                const mpn = String(value).trim();
                mpnMap.set(mpn, []);
                mpnArray.push(mpn);
              });

              // Process data rows after the header row
              for (let r = headerRowIndex + 1; r < rows.length; r++) {
                const row = rows[r];
                if (row.length === 0 || isRowComment(row) || isRowAllEmpty(row))
                  continue;

                for (let colIndex = 0; colIndex < row.length; colIndex++) {
                  const value = row[colIndex];
                  if (value === undefined || String(value).trim() === '')
                    continue;

                  if (colIndex < mpnArray.length) {
                    const mpn = mpnArray[colIndex];
                    const serialNumbers = mpnMap.get(mpn) || [];
                    serialNumbers.push(String(value).trim());
                    mpnMap.set(mpn, serialNumbers);
                  }
                }
              }

              // Convert map to array for easier rendering
              mpnMap.forEach((serialNumbers, mpn) => {
                parsedData.push({
                  mpn,
                  serialNumbers,
                  exists: false,
                  checked: false,
                });
              });

              setParsedData(parsedData);
              setIsLoading(false);
            } catch (error) {
              console.error('Error parsing XLSX file:', error);
              if (error instanceof Error) {
                toast.error(
                  `${t('message.errorParsingCsvFile')}: ${error.message}`
                );
              } else {
                toast.error(t('message.errorParsingCsvFile'));
              }
              setIsLoading(false);
            }
          };

          reader.onerror = () => {
            console.error('Error reading file:', reader.error);
            toast.error(t('message.errorParsingCsvFile'));
            setIsLoading(false);
          };

          reader.readAsArrayBuffer(file);
        }
        // Handle CSV files
        else {
          const reader = new FileReader();

          reader.onload = (e) => {
            const text = e.target?.result as string;

            // Split by newline and filter out empty lines and comment lines
            const lines = text
              .split('\n')
              .map((line) => line.trim())
              .filter((line) => line.length > 0 && !line.startsWith('#'));

            if (lines.length === 0) {
              toast.error(t('message.csvFileIsEmpty'));
              setIsLoading(false);
              return;
            }

            // Detect the delimiter used in the CSV file
            const detectDelimiter = (headerLine: string): string => {
              const delimiters = [',', ';', '\t', '|'];
              const counts = delimiters.map((delimiter) => {
                const count = headerLine.split(delimiter).length - 1;
                return { delimiter, count };
              });

              // Sort by count in descending order
              counts.sort((a, b) => b.count - a.count);

              // Return the delimiter with the highest count, or comma as default
              return counts[0].count > 0 ? counts[0].delimiter : ',';
            };

            // Detect the delimiter from the first line
            const initialDelimiter = detectDelimiter(lines[0]);

            // Helpers
            const isAllEmpty = (values: string[]) =>
              values.every((v) => v.trim() === '');

            // Determine header line index: first line with at least one non-empty value
            let headerIndex = -1;
            for (let i = 0; i < lines.length; i++) {
              const vals = lines[i]
                .split(initialDelimiter)
                .map((val) => val.trim());
              if (!isAllEmpty(vals)) {
                headerIndex = i;
                break;
              }
            }

            if (headerIndex === -1) {
              toast.error(t('message.csvFileIsEmpty'));
              setIsLoading(false);
              return;
            }

            // Re-detect delimiter using the header line for robustness
            const delimiter = detectDelimiter(lines[headerIndex]);

            // Parse the CSV data
            const parsedData: MpnSerialNumberMap[] = [];
            const mpnMap = new Map<string, string[]>();
            const mpnArray: string[] = []; // Store MPNs in order to maintain column mapping

            // Build header (MPNs)
            const headerValues = lines[headerIndex]
              .split(delimiter)
              .map((val) => val.trim());
            headerValues.forEach((value) => {
              if (value === '') return;
              mpnMap.set(value, []);
              mpnArray.push(value);
            });

            // Process data rows after header
            for (let idx = headerIndex + 1; idx < lines.length; idx++) {
              const values = lines[idx]
                .split(delimiter)
                .map((val) => val.trim());
              if (isAllEmpty(values)) continue;

              for (let colIndex = 0; colIndex < values.length; colIndex++) {
                const value = values[colIndex];
                if (value === '') continue;

                if (colIndex < mpnArray.length) {
                  const mpn = mpnArray[colIndex];
                  const serialNumbers = mpnMap.get(mpn) || [];
                  serialNumbers.push(value);
                  mpnMap.set(mpn, serialNumbers);
                }
              }
            }

            // Convert map to array for easier rendering
            mpnMap.forEach((serialNumbers, mpn) => {
              parsedData.push({
                mpn,
                serialNumbers,
                exists: false,
                checked: false,
              });
            });

            setParsedData(parsedData);
            setIsLoading(false);
          };

          reader.onerror = () => {
            console.error('Error reading file:', reader.error);
            toast.error(t('message.errorParsingCsvFile'));
            setIsLoading(false);
          };

          reader.readAsText(file);
        }
      } catch (error) {
        console.error('Error parsing file:', error);
        // Provide more detailed error message
        if (error instanceof Error) {
          toast.error(`${t('message.errorParsingCsvFile')}: ${error.message}`);
        } else {
          toast.error(t('message.errorParsingCsvFile'));
        }
        setIsLoading(false);
      }
    },
    [t]
  );

  // Function to remove an MPN from the parsed data
  const handleRemoveMpn = (mpnToRemove: string) => {
    setParsedData((prevData) =>
      prevData.filter((item) => item.mpn !== mpnToRemove)
    );
    toast.info(t('message.mpnRemovedFromList'));
  };

  // Function to remove a specific serial number from an MPN
  const handleRemoveSerialNumber = (
    mpn: string,
    serialNumberToRemove: string
  ) => {
    setParsedData(
      (prevData) =>
        prevData
          .map((item) => {
            if (item.mpn === mpn) {
              return {
                ...item,
                serialNumbers: item.serialNumbers.filter(
                  (sn) => sn !== serialNumberToRemove
                ),
              };
            }
            return item;
          })
          .filter((item) => item.serialNumbers.length > 0) // Remove MPNs with no serial numbers
    );
    toast.info(t('message.serialNumberRemovedFromList'));
  };

  // Function to update Algolia with the updated vendable
  const updateAlgolia = async (vendableDoc: DTVendable | DTVendable[]) => {
    try {
      // Call the API endpoint to update Algolia
      if (Array.isArray(vendableDoc)) {
        // If it's an array, use the multiple update endpoint
        await nestApiUpdateMultipleVendablesInAlgolia(vendableDoc);
      } else {
        // If it's a single vendable, use the single update endpoint
        await nestApiUpdateVendableInAlgolia(vendableDoc);
      }
      return true;
    } catch (error) {
      console.error('Error updating Algolia:', error);
      return false;
    }
  };

  // Function to save serial numbers to the vendable
  const handleSave = async () => {
    if (parsedData.length === 0) return;

    try {
      setIsLoading(true);

      // Check if all MPNs have been checked
      const uncheckedMpns = parsedData.filter((item) => !item.checked);
      if (uncheckedMpns.length > 0) {
        toast.warning(t('message.waitingForMpnValidation'));
        setIsLoading(false);
        return;
      }

      // Check if any MPNs don't exist
      const invalidMpns = parsedData.filter((item) => !item.exists);
      if (invalidMpns.length > 0) {
        const invalidMpnsList = invalidMpns.map((item) => item.mpn).join(', ');
        toast.error(`${t('message.invalidMpnsFound')}: ${invalidMpnsList}`);
        setIsLoading(false);
        return;
      }

      // Process each MPN separately to update Firestore
      const updatedVendableDocs: DTVendable[] = [];
      const updatePromises = parsedData.map(async (item) => {
        if (item.exists) {
          // Fetch the vendable document by MPN
          const vendableDoc = await getVendableByMpn(item.mpn);

          if (vendableDoc) {
            // Get the current serial numbers from the vendable
            const currentSerialNumbers = vendableDoc.serialNumbers || [];

            // Add new serial numbers
            const newSerialNumbers = [...currentSerialNumbers];
            item.serialNumbers.forEach((sn) => {
              if (!newSerialNumbers.includes(sn)) {
                newSerialNumbers.push(sn);
              }
            });

            // Update the vendable with the new serial numbers
            await updateDocument(`vendable/${vendableDoc.docid}`, {
              serialNumbers: newSerialNumbers,
              serialNumberEnabled: true,
            });

            // Add the updated vendable to the array for Algolia update
            const updatedVendableDoc = {
              ...vendableDoc,
              serialNumbers: newSerialNumbers,
              serialNumberEnabled: true,
            };
            updatedVendableDocs.push(updatedVendableDoc);

            return true;
          }
        }
        return false;
      });

      // Wait for all Firestore updates to complete
      await Promise.all(updatePromises);

      // Update all vendables in Algolia with a single call
      if (updatedVendableDocs.length > 0) {
        await updateAlgolia(updatedVendableDocs);
      }

      toast.success(t('message.serialNumbersUploadedSuccessfully'));
      onClose();
    } catch (error) {
      console.error('Error saving serial numbers:', error);
      toast.error(t('message.errorSavingSerialNumbers'));
    } finally {
      setIsLoading(false);
    }
  };

  // Column definitions for the preview table
  const previewColumns = [
    {
      header: t('general.sku'),
      accessorKey: 'mpn',
      size: 150,
    },
    {
      header: t('general.status'),
      accessorKey: 'exists',
      size: 150,
      Cell: ({ row }: { row: any }) => (
        <div>
          {row.original.checked ? (
            row.original.exists ? (
              <span className="inline-block rounded-full bg-green-100 px-3 py-1 text-sm font-semibold text-green-800">
                {t('general.valid')}
              </span>
            ) : (
              <span className="inline-block rounded-full bg-red-100 px-3 py-1 text-sm font-semibold text-red-800">
                {t('general.invalid')}
              </span>
            )
          ) : (
            <span className="inline-block rounded-full bg-gray-100 px-3 py-1 text-sm font-semibold text-gray-800">
              {t('general.checking')}
            </span>
          )}
        </div>
      ),
    },
    {
      header: 'Count',
      accessorKey: 'count',
      size: 100,
      Cell: ({ row }: { row: any }) => (
        <div className="text-center">
          {Array.isArray(row.original.serialNumbers)
            ? row.original.serialNumbers.length
            : 0}
        </div>
      ),
    },
    {
      header: t('general.serialNumbers'),
      accessorKey: 'serialNumbersDisplay',
      size: 350,
      Cell: ({ row }: { row: any }) => (
        <div className="max-h-40 overflow-y-auto">
          {row.original.serialNumbers.map((sn: string, index: number) => (
            <div
              key={index}
              className="relative z-10 mb-1 flex items-center justify-between rounded bg-gray-100 px-2 py-1"
            >
              <span>{sn}</span>
              <ButtonIcon
                kind="bgGreen"
                Icon={TrashIcon}
                onClick={() => handleRemoveSerialNumber(row.original.mpn, sn)}
                label={''}
                className="min-w-3 absolute top-1 right-0 h-4 w-4"
              />
            </div>
          ))}
        </div>
      ),
    },
    {
      header: t('general.actions'),
      accessorKey: 'actions',
      size: 100,
      Cell: ({ row }: { row: any }) => (
        <div className="relative z-10 flex justify-center">
          <ButtonIcon
            kind="bgRed"
            Icon={TrashIcon}
            onClick={() => handleRemoveMpn(row.original.mpn)}
            label={''}
            className={'absolute top-[-2px] left-3'}
          />
        </div>
      ),
    },
  ];

  return (
    <Modal
      kind="generic"
      settings={{
        title: t('general.uploadSerialNumbersCsv'),
        handleClose: onClose,
      }}
      className="w-full max-w-4xl rounded-md border shadow-lg"
    >
      <Formik
        initialValues={initialValues}
        onSubmit={() => {
          handleSave();
        }}
      >
        {() => {
          return (
            <Form>
              <div className="relative bg-white p-6">
                {isLoading && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-white bg-opacity-70">
                    <div className="flex flex-col items-center">
                      <LoadingSpinner size="sm" />
                      <p className="text-primary-700 mt-2 font-semibold">
                        {t('general.loading')}...
                      </p>
                    </div>
                  </div>
                )}

                <div className="mb-6">
                  <p className="font-nunito mb-4 text-sm text-gray-700">
                    {t('message.uploadCsvDescription')}
                  </p>

                  <div className="relative mb-7 flex items-center">
                    <p className="font-nunito absolute right-[100px] mr-2 text-sm text-gray-700">
                      Not sure about the format?
                    </p>
                    <Button
                      kind="primary"
                      type="button"
                      size={'sm'}
                      onClick={handleDownloadSampleFile}
                      className="font-nunito absolute right-0 w-[100px] items-center gap-1 rounded-md px-2 py-0.5 text-sm"
                    >
                      Sample File
                    </Button>
                  </div>

                  <div className="mb-6 rounded-lg border border-gray-200 p-4">
                    <div className="flex flex-col gap-2">
                      <label
                        htmlFor="serial-file-input"
                        className="font-nunito text-sm font-medium text-gray-700"
                      >
                        {t('general.csvFileWithSerialNumbers')}
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          id="serial-file-input"
                          type="file"
                          accept=".csv,text/csv,application/vnd.ms-excel,application/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.numbers"
                          onChange={handleFileChange}
                          className="file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 block
                            w-full text-sm text-gray-500
                            file:mr-4 file:rounded-md
                            file:border-0 file:py-2
                            file:px-4 file:text-sm
                            file:font-semibold"
                        />
                        {selectedFile && (
                          <div className="flex items-center gap-1 rounded-md bg-gray-100 px-2 py-1 text-sm">
                            <span>{selectedFile.name}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {parsedData.length > 0 && (
                    <div className="mb-6">
                      <h3 className="font-nunito mb-4 text-lg font-semibold">
                        {t('general.serialNumbersFromCsv')}
                      </h3>
                      <div className="overflow-x-auto rounded-lg border border-gray-200 shadow">
                        <Table
                          name="serial-numbers-preview-table"
                          columns={previewColumns}
                          data={parsedData}
                          enableColumnFilters={false}
                          enableTopToolbar={false}
                          enableBottomToolbar={false}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <Button
                    kind="red"
                    type="button"
                    onClick={onClose}
                    className="font-nunito rounded-md px-4 py-2"
                  >
                    {t('general.cancel')}
                  </Button>
                  <Button
                    kind="primary"
                    type="submit"
                    disabled={parsedData.length === 0 || isLoading}
                    className="font-nunito flex items-center gap-2 rounded-md px-4 py-2"
                  >
                    {isLoading ? t('general.saving') : t('general.save')}
                  </Button>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default SerialNumberUploadModal;
